import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ShoppingCart, Plus, Minus, X, ArrowLeft, Check, Mail, Phone, MapPin, Loader2, AlertCircle, Smartphone, QrCode, Copy } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import { QRCodeCanvas } from "qrcode.react";
import { useShopAuth, shopApi } from "../lib/shopAuth";
import { useI18n } from "../lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL;

// Résout une URL photo : si elle commence par "/" → préfixe avec le backend public
function resolvePhotoUrl(url, width) {
  if (!url) return "";
  let full = url.startsWith("/") ? `${API}${url}` : url;
  if (width && full.includes(`${API}/api/shop/products/`)) {
    const sep = full.includes("?") ? "&" : "?";
    full += `${sep}w=${width}`;
  }
  return full;
}

const STORAGE_KEY = "maison-thai-cart-v1";
const FREE_DELIVERY = 70;
const DELIVERY_FEE = 10;
const ISOTHERM_MIN = 3;
const ISOTHERM_MAX = 5;
const SHOP_CONTACT_EMAIL = "contact@maison-thai.ch";

// Construit un mailto: pré-rempli avec la liste des plats du panier.
// Utilisé quand le client souhaite un envoi national hors-range (1-2 ou >5 barquettes).
function buildIsothermContactMailto(items, count, subtotal, customerEmail) {
  const subject = `Envoi postal Suisse — ${count} barquette${count > 1 ? "s" : ""}`;
  const lines = [
    "Bonjour,",
    "",
    `J'aimerais commander ${count} barquette${count > 1 ? "s" : ""} en envoi postal isotherme :`,
    "",
    ...items.map((it) => `  • ${it.quantity} × ${it.name}${it.sku ? ` (réf. ${it.sku})` : ""}`),
    "",
    `Sous-total panier : ${subtotal.toFixed(2)} CHF`,
    "",
    "Pouvez-vous m'indiquer la solution logistique la plus adaptée et le délai de livraison ?",
    "",
    "Merci d'avance,",
    customerEmail ? `(compte boutique : ${customerEmail})` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return `mailto:${SHOP_CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
}

const COLORS = {
  bg: "#0A0A0A", surface: "#141414", primary: "#F2ECE4", secondary: "#A6A29C", muted: "#737373",
  chili: "#E54D2E", chiliHover: "#F06B52", gold: "#C1A063", basil: "#88A066",
};

// ============== CMS Boutique (textes éditables par l'admin) ==============
// Cache module-level pour ne fetcher qu'une fois par session navigateur
let _cmsCache = null;
let _cmsPromise = null;

async function fetchCmsBlocks() {
  if (_cmsCache) return _cmsCache;
  if (_cmsPromise) return _cmsPromise;
  _cmsPromise = fetch(`${API}/api/shop/cms`)
    .then((r) => r.ok ? r.json() : [])
    .then((data) => {
      _cmsCache = data || [];
      // Index par slug pour lookup O(1)
      _cmsCache._bySlug = {};
      for (const b of _cmsCache) _cmsCache._bySlug[b.slug] = b;
      return _cmsCache;
    })
    .catch(() => { _cmsCache = []; _cmsCache._bySlug = {}; return _cmsCache; });
  return _cmsPromise;
}

function useCmsBlock(slug) {
  const [block, setBlock] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchCmsBlocks().then((blocks) => {
      if (alive) setBlock(blocks?._bySlug?.[slug] || null);
    });
    return () => { alive = false; };
  }, [slug]);
  return block;
}

/**
 * Bannière CMS affichée sur la boutique. Récupère le contenu depuis l'API.
 * - Si le bloc n'existe pas ou est désactivé : ne rend rien.
 * - Variants : info (doré), warning (chili), promo (basilic).
 * - Les retours à la ligne du contenu sont préservés (white-space: pre-line).
 */
export function ShopCmsBanner({ slug, className = "" }) {
  const block = useCmsBlock(slug);
  if (!block || !block.enabled || !(block.content || "").trim()) return null;

  const variantStyles = {
    info:    { border: COLORS.gold,   bg: "rgba(193,160,99,0.08)",  accent: COLORS.gold },
    warning: { border: COLORS.chili,  bg: "rgba(229,77,46,0.08)",   accent: COLORS.chili },
    promo:   { border: COLORS.basil,  bg: "rgba(136,160,102,0.08)", accent: COLORS.basil },
  };
  const v = variantStyles[block.variant] || variantStyles.info;

  return (
    <div
      className={`px-5 py-4 border-l-2 ${className}`}
      style={{ borderColor: v.border, background: v.bg }}
      data-testid={`cms-banner-${slug}`}
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: v.accent }}>
        {block.title}
      </p>
      <p className="text-sm leading-relaxed font-body" style={{ color: COLORS.primary, whiteSpace: "pre-line" }}>
        {block.content}
      </p>
    </div>
  );
}

// ============== Cart Context ==============
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(50, next[idx].quantity + qty) };
        return next;
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        photo_url: product.photo_url,
        unit_price: product.price_online,
        quantity: qty,
      }];
    });
  };

  const setQty = (productId, qty) => {
    setItems((prev) => prev.map((it) => it.product_id === productId ? { ...it, quantity: Math.max(1, Math.min(50, qty)) } : it));
  };
  const remove = (productId) => setItems((prev) => prev.filter((it) => it.product_id !== productId));
  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, it) => s + (it.unit_price || 0) * it.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

// Petit badge panier (utilisé dans le header) — exposé pour ShopLayout
export function CartBadge() {
  const { count } = useCart();
  const { t } = useI18n();
  return (
    <Link
      to="/boutique/panier"
      data-testid="cart-badge"
      className="relative flex items-center gap-2 text-xs uppercase tracking-[0.2em] transition-colors"
      style={{ color: count > 0 ? COLORS.primary : COLORS.muted }}
    >
      <ShoppingCart className="w-4 h-4" />
      <span className="hidden md:inline">{t("header.cart")}</span>
      {count > 0 && (
        <span
          className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold rounded-full"
          style={{ background: COLORS.chili, color: "#FFFFFF" }}
          data-testid="cart-count"
        >
          {count}
        </span>
      )}
    </Link>
  );
}

// ============== Cart page ==============
export function CartPage() {
  const { items, setQty, remove, subtotal, count } = useCart();
  const { customer: authCustomer } = useShopAuth() || {};
  const { t } = useI18n();
  const navigate = useNavigate();
  const reorderMissing = useLocation().state?.reorderMissing || [];
  const [soldOutIds, setSoldOutIds] = useState([]);
  useEffect(() => {
    // Le message « non ajoutés » ne doit pas survivre à un rechargement de la page
    if (window.history.state?.usr?.reorderMissing) window.history.replaceState({ ...window.history.state, usr: null }, "");
    fetch(`${API}/api/shop/products`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setSoldOutIds((list || []).filter((p) => p.sold_out).map((p) => p.id)))
      .catch(() => {});
  }, []);
  const hasSoldOut = items.some((it) => soldOutIds.includes(it.product_id));

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="cart-empty">
        <ShoppingCart className="w-12 h-12 mx-auto mb-6" style={{ color: COLORS.muted }} />
        <h1 className="font-heading text-4xl mb-4" style={{ color: COLORS.primary }}>{t("cart.empty_title")}</h1>
        <p className="font-body text-sm mb-10" style={{ color: COLORS.secondary }}>
          {t("cart.empty_body")}
        </p>
        <Link to="/boutique" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em]" style={{ background: COLORS.chili, color: "#FFFFFF" }}>
          {t("cart.empty_cta")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16" data-testid="cart-page">
      <button onClick={() => navigate("/boutique")} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-6 sm:mb-8" style={{ color: COLORS.muted }}>
        <ArrowLeft className="w-3.5 h-3.5" /> {t("cart.continue_shopping")}
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-6 sm:mb-8" style={{ color: COLORS.primary }}>{t("cart.title")}</h1>
      {reorderMissing.length > 0 && (
        <div className="p-4 mb-6 text-sm flex items-start gap-2" style={{ border: "1px solid rgba(229,77,46,0.35)", background: "rgba(229,77,46,0.08)", color: COLORS.primary }} data-testid="cart-reorder-missing">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#E54D2E" }} />
          <span>{t("cart.reorder_missing")} : {reorderMissing.join(", ")}</span>
        </div>
      )}
      <ShopCmsBanner slug="delais_livraison" className="mb-8 sm:mb-10" />
      <ShopCmsBanner slug="promo_moment" className="mb-8 sm:mb-10" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 sm:gap-12">
        {/* Lignes */}
        <div className="space-y-4 sm:space-y-6">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-3 sm:gap-4 pb-4 sm:pb-6 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }} data-testid={`cart-item-${it.product_id}`}>
              {it.photo_url && (
                <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 flex-shrink-0 overflow-hidden" style={{ background: "#111" }}>
                  <img src={resolvePhotoUrl(it.photo_url, 200)} alt={it.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col">
                <h3 className="font-heading text-lg sm:text-xl md:text-2xl leading-tight" style={{ color: COLORS.primary }}>{it.name}</h3>
                <p className="text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: COLORS.muted }}>{t("cart.ref")} {it.sku}</p>
                {soldOutIds.includes(it.product_id) && (
                  <p className="text-[11px] uppercase tracking-[0.2em] mt-1 font-semibold" style={{ color: COLORS.chili }} data-testid={`cart-item-sold-out-${it.product_id}`}>
                    {t("add_to_cart.sold_out")}
                  </p>
                )}
                <div className="flex items-center justify-between mt-auto pt-3 sm:pt-4 gap-3 flex-wrap">
                  <div className="flex items-center border" style={{ borderColor: "rgba(242,236,228,0.15)" }}>
                    <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-white/5" data-testid={`qty-minus-${it.product_id}`}>
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-10 text-center font-mono text-sm" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                    <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-white/5" data-testid={`qty-plus-${it.product_id}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <span className="font-body text-lg font-medium ml-auto" style={{ color: COLORS.gold }}>
                    {((it.unit_price || 0) * it.quantity).toFixed(2)} CHF
                  </span>
                  <button onClick={() => remove(it.product_id)} className="opacity-50 hover:opacity-100 transition-opacity" data-testid={`remove-${it.product_id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Récap */}
        <aside className="border p-6 md:p-8 self-start lg:sticky lg:top-32" style={{ borderColor: "rgba(242,236,228,0.1)", background: COLORS.surface }}>
          <h2 className="font-heading text-2xl mb-6" style={{ color: COLORS.primary }}>{t("cart.summary")}</h2>
          <div className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.secondary }}>{t("cart.subtotal")}</span>
            <span className="font-mono" style={{ color: COLORS.primary }}>{subtotal.toFixed(2)} CHF</span>
          </div>
          <div className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.secondary }}>{t("cart.delivery")}</span>
            <span className="text-xs uppercase tracking-wider" style={{ color: COLORS.muted }}>{t("cart.delivery_at_checkout")}</span>
          </div>
          {subtotal < FREE_DELIVERY && (
            <div className="mt-4 p-3 text-xs" style={{ background: "rgba(193,160,99,0.08)", borderLeft: `2px solid ${COLORS.gold}`, color: COLORS.gold }} data-testid="free-delivery-hint">
              <strong>{t("cart.free_delivery_hint_valais_prefix")}</strong> <strong>{(FREE_DELIVERY - subtotal).toFixed(2)} CHF</strong> {t("cart.free_delivery_hint_valais_suffix")}
            </div>
          )}
          {/* Info envoi national isotherme — affichée pour tous les paniers >0 */}
          <div
            className="mt-3 p-3 text-xs leading-relaxed"
            style={{ background: "rgba(136,160,102,0.07)", borderLeft: `2px solid ${COLORS.basil}`, color: COLORS.secondary }}
            data-testid="cart-isotherm-hint"
          >
            <strong style={{ color: COLORS.primary }}>{t("cart.isotherm_hint_title")} :</strong> {t("cart.isotherm_hint_body")}
            {(count < ISOTHERM_MIN || count > ISOTHERM_MAX) && (
              <div className="mt-2">
                {t("cart.isotherm_out_of_range_prefix")} ({count} {count === 1 ? t("cart.barquette_unit") : t("cart.barquette_plural")}) {t("cart.isotherm_out_of_range_suffix")} :{" "}
                <a
                  href={buildIsothermContactMailto(items, count, subtotal, authCustomer?.email)}
                  className="underline hover:no-underline"
                  style={{ color: COLORS.gold }}
                  data-testid="cart-isotherm-contact-link"
                >
                  {t("cart.send_selection_email")}
                </a>
                .
              </div>
            )}
          </div>
          {hasSoldOut && (
            <p className="mt-4 text-xs" style={{ color: COLORS.chili }} data-testid="cart-sold-out-hint">{t("cart.sold_out_hint")}</p>
          )}
          <button
            onClick={() => navigate("/boutique/checkout")}
            disabled={hasSoldOut}
            className="w-full mt-6 py-4 px-6 text-xs uppercase tracking-[0.25em] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: COLORS.chili, color: "#FFFFFF" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.chiliHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = COLORS.chili; }}
            data-testid="checkout-btn"
          >
            {t("cart.checkout_btn")}
          </button>
        </aside>
      </div>
    </div>
  );
}

// ============== Add-to-cart button (utilisé dans la page détail produit) ==============
export function AddToCartButton({ product, onAdded }) {
  const { add, items } = useCart();
  const { t } = useI18n();
  const existing = items.find((it) => it.product_id === product.id);
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = () => {
    add(product, 1);
    setJustAdded(true);
    setTimeout(() => (onAdded ? onAdded() : setJustAdded(false)), onAdded ? 600 : 1500);
  };

  if (product.sold_out) {
    return (
      <div
        className="block w-full text-center py-5 px-8 text-sm uppercase tracking-[0.25em] font-semibold border"
        style={{ borderColor: "rgba(242,236,228,0.2)", color: COLORS.secondary }}
        data-testid="add-to-cart-sold-out"
      >
        {t("add_to_cart.sold_out")}
      </div>
    );
  }

  return (
    <button
      onClick={handleAdd}
      className="block w-full text-center py-5 px-8 text-sm uppercase tracking-[0.25em] font-semibold transition-all hover:tracking-[0.3em]"
      style={{ background: justAdded ? COLORS.basil : COLORS.chili, color: "#FFFFFF" }}
      data-testid="add-to-cart-btn"
    >
      {justAdded ? (
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4" /> {t("add_to_cart.added")}</span>
      ) : existing ? (
        `${t("add_to_cart.again")} · ${existing.quantity} ${t("add_to_cart.in_cart")}`
      ) : (
        `${t("add_to_cart.btn")} · ${product.price_online?.toFixed(2)} CHF`
      )}
    </button>
  );
}

// ============== Checkout page ==============
export function CheckoutPage() {
  const { items, subtotal, count, clear } = useCart();
  const navigate = useNavigate();
  const { customer: authCustomer } = useShopAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState("pickup"); // pickup | delivery
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "", postal_code: "", city: "", notes: "" });
  // Phase 87n — Date d'arrivée souhaitée (clients locaux / entreprises)
  const [desiredDate, setDesiredDate] = useState("");
  const [desiredSlot, setDesiredSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  // Phase 2 : commande créée, paiement PayPal en attente
  const [pendingOrder, setPendingOrder] = useState(null);
  // Configuration PayPal (Client ID public récupéré du backend)
  const [paypalCfg, setPaypalCfg] = useState(null);
  // Phase 101ey — Statut TWINT (activé ou non par l'admin en /parametres)
  const [twintCfg, setTwintCfg] = useState(null);

  // Auth gate : redirige vers login si non connecté
  useEffect(() => {
    if (authCustomer === false) {
      navigate(`/boutique/login?next=${encodeURIComponent("/boutique/checkout")}`, { replace: true });
    }
  }, [authCustomer, navigate]);

  const savedAddresses = (authCustomer && authCustomer.addresses) || [];
  const defaultAddr = savedAddresses.find((a) => a.is_default) || savedAddresses[0];
  // Pré-remplit les champs client à partir du profil
  useEffect(() => {
    if (authCustomer && authCustomer !== false) {
      setCustomer((c) => ({
        ...c,
        name: c.name || authCustomer.name || "",
        email: c.email || authCustomer.email || "",
        phone: c.phone || authCustomer.phone || "",
        address: c.address || defaultAddr?.address || authCustomer.address || "",
        postal_code: c.postal_code || defaultAddr?.postal_code || authCustomer.postal_code || "",
        city: c.city || defaultAddr?.city || authCustomer.city || "",
      }));
    }
  }, [authCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickSavedAddress = (id) => {
    const a = savedAddresses.find((x) => x.id === id);
    if (!a) return;
    setCustomer((c) => ({
      ...c,
      address: a.address, postal_code: a.postal_code, city: a.city,
      phone: a.phone || c.phone,
      name: a.name || c.name,
    }));
  };

  // Si panier vide, redirige (sauf si on est en phase paiement)
  useEffect(() => {
    if (items.length === 0 && !pendingOrder) navigate("/boutique", { replace: true });
  }, [items.length, navigate, pendingOrder]);

  // Charge la config PayPal une seule fois (Client ID public + mode)
  useEffect(() => {
    fetch(`${API}/api/shop/paypal/status`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setPaypalCfg(data); })
      .catch(() => setPaypalCfg({ configured: false }));
  }, []);

  // Phase 101ey — Statut TWINT (activé/configuré côté admin)
  useEffect(() => {
    fetch(`${API}/api/shop/twint/status`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setTwintCfg(data); })
      .catch(() => setTwintCfg({ enabled: false, configured: false }));
  }, []);

  // Récupère un devis de livraison en temps réel quand le CP / sous-total / mode changent
  // Debounce 350ms sur le code postal pour éviter une requête par frappe
  useEffect(() => {
    if (mode === "pickup") {
      setDeliveryQuote({ delivery_fee: 0, delivery_label: "pickup", message: "Retrait sur place — Rue de Parcouret 22, Conthey" });
      return;
    }
    const cp = (customer.postal_code || "").trim();
    if (!cp) {
      setDeliveryQuote(null);
      return;
    }
    const handle = setTimeout(() => {
      const params = new URLSearchParams({ postal_code: cp, subtotal: String(subtotal), mode });
      fetch(`${API}/api/shop/delivery-quote?${params.toString()}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setDeliveryQuote(data); })
        .catch(() => { /* fallback silencieux */ });
    }, 350);
    return () => clearTimeout(handle);
  }, [mode, customer.postal_code, subtotal]);

  // Frais effectifs (préfère le quote backend, sinon fallback côté client)
  const deliveryFee = deliveryQuote
    ? deliveryQuote.delivery_fee
    : (mode === "delivery" && subtotal < FREE_DELIVERY ? DELIVERY_FEE : 0);
  const total = subtotal + deliveryFee;

  const set = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!customer.name.trim() || !customer.email.trim() || !customer.phone.trim()) {
      setError("Nom, email et téléphone sont obligatoires."); return;
    }
    if (mode === "delivery" && (!customer.address.trim() || !customer.postal_code.trim() || !customer.city.trim())) {
      setError("Adresse de livraison complète requise (rue, NPA, localité)."); return;
    }
    if (!paypalCfg?.configured) {
      setError("Le paiement en ligne n'est pas encore configuré. Veuillez réessayer plus tard ou nous contacter au +41 27 346 07 75.");
      return;
    }
    setSubmitting(true);
    try {
      // Phase 87r — Passage par shopApi (axios) au lieu de fetch natif :
      // bénéficie du retry transparent sur 401 (refresh access_token via cookie).
      // Évite la déconnexion brutale du client au moment crucial du checkout.
      const { data: order } = await shopApi.post("/orders", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        customer,
        delivery_mode: mode,
        desired_delivery_date: desiredDate || null,
        desired_delivery_slot: desiredSlot || null,
      });
      // Passage en mode "paiement" : on affiche les boutons PayPal inline
      setPendingOrder(order);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        // Si on est arrivé ici malgré le refresh, le refresh a échoué : refresh_token
        // probablement expiré (> 30 jours sans connexion). On redirige vers login.
        navigate(`/boutique/login?next=${encodeURIComponent("/boutique/checkout")}`, { replace: true });
        return;
      }
      if (status === 403) {
        setError("Votre email n'est pas encore vérifié. Cliquez sur le lien envoyé dans votre boîte de réception, ou demandez un nouveau lien depuis votre compte.");
        return;
      }
      setError(typeof detail === "string" ? detail : (err.message || "Erreur lors de la commande"));
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && !pendingOrder) return null;

  // ============== Écran paiement PayPal ==============
  if (pendingOrder) {
    return (
      <PayPalPaymentScreen
        order={pendingOrder}
        items={items}
        deliveryFee={pendingOrder.delivery_fee ?? deliveryFee}
        paypalCfg={paypalCfg}
        twintCfg={twintCfg}
        onCancel={() => setPendingOrder(null)}
        onPaid={() => { clear(); navigate(`/boutique/paiement/succes?order_id=${pendingOrder.id}`, { replace: true }); }}
        onTwintInitiated={() => { clear(); navigate(`/boutique/paiement/twint-en-attente?order_id=${pendingOrder.id}`, { replace: true }); }}
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16" data-testid="checkout-page">
      <button onClick={() => navigate("/boutique/panier")} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-6 sm:mb-8" style={{ color: COLORS.muted }}>
        <ArrowLeft className="w-3.5 h-3.5" /> {t("checkout.back_to_cart")}
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-6 sm:mb-8" style={{ color: COLORS.primary }}>{t("checkout.title")}</h1>
      <ShopCmsBanner slug="delais_livraison" className="mb-8 sm:mb-10" />

      {/* Bandeau : email non vérifié → empêche le checkout */}
      {authCustomer && authCustomer !== false && !authCustomer.email_verified && (
        <div
          className="p-4 mb-8 flex items-start gap-3 text-sm"
          style={{ background: "rgba(229,77,46,0.08)", border: "1px solid rgba(229,77,46,0.35)", color: COLORS.chili }}
          data-testid="checkout-verify-warning"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-1">{t("checkout.verify_email_warning_title")}</p>
            <p style={{ color: COLORS.secondary }}>
              {t("checkout.verify_email_warning_body")}
            </p>
            <Link to="/boutique/compte" className="text-xs uppercase tracking-[0.2em] underline mt-2 inline-block" style={{ color: COLORS.gold }} data-testid="checkout-go-account">
              {t("checkout.verify_email_warning_cta")}
            </Link>
          </div>
        </div>
      )}

      {/* Phase 98h — Bandeau : compte pro en attente de validation → bloque le checkout */}
      {authCustomer && authCustomer !== false && authCustomer.pro_status === "pending" && (
        <div
          className="p-4 mb-8 flex items-start gap-3 text-sm"
          style={{ background: "rgba(193,160,99,0.10)", border: `1px solid ${COLORS.gold}`, color: COLORS.gold }}
          data-testid="checkout-pro-pending-warning"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-1">Compte pro en cours de validation</p>
            <p style={{ color: COLORS.secondary }}>
              Votre candidature professionnelle est en attente d&apos;examen (24-48h typique). Le passage de commande sera disponible dès la validation de votre compte par notre équipe.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 sm:gap-12">
        <div className="space-y-6 sm:space-y-8">
          {/* Mode */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.25em] mb-3 sm:mb-4" style={{ color: COLORS.gold }}>{t("checkout.mode_reception")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { v: "pickup", title: t("checkout.mode_pickup_title"), desc: t("checkout.mode_pickup_desc") },
                { v: "delivery", title: t("checkout.mode_delivery_title"), desc: t("checkout.mode_delivery_desc") },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMode(opt.v)}
                  data-testid={`mode-${opt.v}`}
                  className="text-left p-4 sm:p-5 border transition-all"
                  style={{
                    borderColor: mode === opt.v ? COLORS.chili : "rgba(242,236,228,0.12)",
                    background: mode === opt.v ? "rgba(229,77,46,0.06)" : "transparent",
                  }}
                >
                  <div className="font-heading text-lg sm:text-xl" style={{ color: COLORS.primary }}>{opt.title}</div>
                  <div className="text-xs mt-1" style={{ color: COLORS.secondary }}>{opt.desc}</div>
                </button>
              ))}
            </div>
            {/* Avertissement envoi isotherme hors-zone Valais : 3-5 barquettes uniquement */}
            {mode === "delivery" &&
              deliveryQuote &&
              !deliveryQuote.is_local_zone &&
              (count < ISOTHERM_MIN || count > ISOTHERM_MAX) && (
                <div
                  className="mt-3 p-3 sm:p-4 flex items-start gap-3 text-xs sm:text-sm"
                  style={{
                    background: "rgba(229,77,46,0.08)",
                    border: "1px solid rgba(229,77,46,0.35)",
                    color: COLORS.chili,
                  }}
                  data-testid="isotherm-range-warning"
                >
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold mb-1">
                      {count === 1
                        ? t("checkout.isotherm_range_warning_title_one")
                        : t("checkout.isotherm_range_warning_title_many", { count })}
                    </p>
                    <p style={{ color: COLORS.secondary }}>
                      {count < ISOTHERM_MIN
                        ? t("checkout.isotherm_range_warning_body_lt")
                        : t("checkout.isotherm_range_warning_body_gt")}
                    </p>
                    <a
                      href={buildIsothermContactMailto(items, count, subtotal, customer.email || authCustomer?.email)}
                      className="text-xs uppercase tracking-[0.2em] underline mt-2 inline-block"
                      style={{ color: COLORS.gold }}
                      data-testid="isotherm-contact-link"
                    >
                      {t("checkout.isotherm_send_selection")}
                    </a>
                  </div>
                </div>
              )}
          </section>

          {/* Identité */}
          <section className="space-y-4">
            <h2 className="text-xs uppercase tracking-[0.25em]" style={{ color: COLORS.gold }}>{t("checkout.your_info")}</h2>
            <Field icon={<Mail className="w-4 h-4" />} label={t("checkout.name")} value={customer.name} onChange={(v) => set("name", v)} testid="cust-name" autoComplete="name" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field icon={<Mail className="w-4 h-4" />} label={t("checkout.email")} type="email" value={customer.email} onChange={(v) => set("email", v)} testid="cust-email" autoComplete="email" />
              <Field icon={<Phone className="w-4 h-4" />} label={t("checkout.phone")} type="tel" value={customer.phone} onChange={(v) => set("phone", v)} testid="cust-phone" autoComplete="tel" placeholder="+41 …" />
            </div>
          </section>

          {/* Adresse (delivery only) */}
          {mode === "delivery" && (
            <section className="space-y-4" data-testid="delivery-block">
              <h2 className="text-xs uppercase tracking-[0.25em]" style={{ color: COLORS.gold }}>Adresse de livraison</h2>
              {savedAddresses.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="saved-addresses">
                  {savedAddresses.map((a) => {
                    const on = a.address === customer.address && a.postal_code === customer.postal_code;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => pickSavedAddress(a.id)}
                        className="text-left px-3 py-2 text-xs transition-colors"
                        style={{ border: `1px solid ${on ? COLORS.gold : "rgba(242,236,228,0.15)"}`, color: on ? COLORS.gold : COLORS.secondary }}
                        data-testid={`saved-address-${a.id}`}
                      >
                        <span className="block uppercase tracking-[0.15em] font-semibold">{a.label || "Adresse"}</span>
                        <span className="block">{a.address}, {a.postal_code} {a.city}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <Field icon={<MapPin className="w-4 h-4" />} label="Rue et numéro *" value={customer.address} onChange={(v) => set("address", v)} testid="cust-address" autoComplete="street-address" />
              <div className="grid grid-cols-3 gap-4">
                <Field label="NPA *" value={customer.postal_code} onChange={(v) => set("postal_code", v)} testid="cust-postal" autoComplete="postal-code" />
                <div className="col-span-2">
                  <Field label="Localité *" value={customer.city} onChange={(v) => set("city", v)} testid="cust-city" autoComplete="address-level2" />
                </div>
              </div>
              <p className="text-[11px] italic" style={{ color: COLORS.muted }}>
                Suisse uniquement. Livraison sous 24h via La Poste PostPac Priority (chaîne du froid maintenue).
              </p>
            </section>
          )}

          {/* Phase 87n — Date d'arrivée souhaitée (clients locaux & entreprises) */}
          <section data-testid="desired-delivery-section">
            <h2 className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: COLORS.gold }}>
              Date d'arrivée souhaitée <span style={{ color: COLORS.muted, textTransform: "none", letterSpacing: 0 }}>(optionnel)</span>
            </h2>
            <p className="text-[11px] italic mb-3" style={{ color: COLORS.muted }}>
              Utile si vous êtes <strong>en région</strong> ou une <strong>entreprise</strong> qui souhaite recevoir le lot un jour précis (ex&nbsp;: commande aujourd'hui pour jeudi midi). Sinon, expédition standard La Poste (J+1 si commandé avant 10h).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] mb-1.5" style={{ color: COLORS.muted }}>Date</label>
                <input
                  type="date"
                  value={desiredDate}
                  onChange={(e) => setDesiredDate(e.target.value)}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })()}
                  className="w-full bg-transparent border px-4 py-3 text-sm focus:outline-none focus:border-white/30 transition-colors"
                  style={{ borderColor: "rgba(242,236,228,0.12)", color: COLORS.primary, colorScheme: "dark" }}
                  data-testid="desired-delivery-date"
                />
              </div>
              <div>
                <label className="block text-[11px] mb-1.5" style={{ color: COLORS.muted }}>Créneau</label>
                <select
                  value={desiredSlot}
                  onChange={(e) => setDesiredSlot(e.target.value)}
                  className="w-full bg-transparent border px-4 py-3 text-sm focus:outline-none focus:border-white/30 transition-colors"
                  style={{ borderColor: "rgba(242,236,228,0.12)", color: COLORS.primary, colorScheme: "dark" }}
                  data-testid="desired-delivery-slot"
                >
                  <option value="" style={{ color: "#000" }}>— Indifférent —</option>
                  <option value="matin" style={{ color: "#000" }}>Matin (08h–11h)</option>
                  <option value="midi" style={{ color: "#000" }}>Midi (11h–14h)</option>
                  <option value="soir" style={{ color: "#000" }}>Soir (17h–20h)</option>
                  <option value="autre" style={{ color: "#000" }}>Autre (précisez ci-dessous)</option>
                </select>
              </div>
            </div>
          </section>

          {/* Notes libres */}
          <section>
            <h2 className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: COLORS.gold }}>Message (optionnel)</h2>
            <textarea
              rows={3}
              value={customer.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Instructions particulières, intolérances, horaires de retrait souhaités…"
              className="w-full bg-transparent border px-4 py-3 text-sm focus:outline-none focus:border-white/30 transition-colors"
              style={{ borderColor: "rgba(242,236,228,0.12)", color: COLORS.primary }}
              data-testid="cust-notes"
            />
          </section>

          {error && (
            <div className="flex items-start gap-2 p-4 border" style={{ borderColor: "rgba(229,77,46,0.4)", background: "rgba(229,77,46,0.05)" }} data-testid="form-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: COLORS.chili }} />
              <p className="text-sm" style={{ color: COLORS.chili }}>{error}</p>
            </div>
          )}
        </div>

        {/* Récap droite */}
        <aside className="border p-6 md:p-8 self-start lg:sticky lg:top-32" style={{ borderColor: "rgba(242,236,228,0.1)", background: COLORS.surface }}>
          <h2 className="font-heading text-2xl mb-6" style={{ color: COLORS.primary }}>{t("checkout.total")}</h2>
          <div className="space-y-2 text-sm pb-4 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between gap-2">
                <span style={{ color: COLORS.secondary }}>{it.quantity} × {it.name.replace(", riz thaï", "").substring(0, 28)}</span>
                <span className="font-mono whitespace-nowrap" style={{ color: COLORS.primary }}>{((it.unit_price || 0) * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm py-3 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.secondary }}>{t("cart.subtotal")}</span>
            <span className="font-mono" style={{ color: COLORS.primary }} data-testid="recap-subtotal">{subtotal.toFixed(2)} CHF</span>
          </div>
          <div className="flex justify-between text-sm py-3 border-b" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
            <span style={{ color: COLORS.secondary }}>{t("cart.delivery")}</span>
            <span className="font-mono" style={{ color: deliveryFee > 0 ? COLORS.primary : COLORS.basil }} data-testid="recap-delivery">
              {deliveryFee > 0 ? `${deliveryFee.toFixed(2)} CHF` : (mode === "delivery" ? t("checkout.delivery_free") : "—")}
            </span>
          </div>
          {deliveryQuote?.message && (
            <p
              className="text-[11px] leading-relaxed -mt-1 pb-2"
              style={{
                color: deliveryQuote.delivery_label === "local_free" || deliveryQuote.delivery_label === "national_free"
                  ? COLORS.basil
                  : (deliveryQuote.is_local_zone ? COLORS.gold : COLORS.muted),
              }}
              data-testid="delivery-message"
            >
              {deliveryQuote.delivery_label === "local_free" && "🌿 "}
              {deliveryQuote.delivery_label === "national_free" && "✓ "}
              {deliveryQuote.message}
            </p>
          )}
          <div className="flex justify-between text-lg py-4">
            <span style={{ color: COLORS.primary }}>{t("checkout.total_ttc")}</span>
            <span className="font-mono font-semibold" style={{ color: COLORS.gold }} data-testid="recap-total">{total.toFixed(2)} CHF</span>
          </div>
          <button
            type="submit"
            disabled={submitting || !paypalCfg?.configured || (authCustomer && authCustomer !== false && authCustomer.pro_status === "pending")}
            className="w-full mt-2 py-4 px-6 text-xs uppercase tracking-[0.25em] font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: COLORS.chili, color: "#FFFFFF" }}
            data-testid="submit-order-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("checkout.submitting")}</> : (
              authCustomer && authCustomer !== false && authCustomer.pro_status === "pending"
                ? "Commande bloquée — compte pro en attente"
                : `${t("checkout.submit_btn")} · ${total.toFixed(2)} CHF`
            )}
          </button>
          <p className="text-[10px] uppercase tracking-[0.2em] text-center mt-3" style={{ color: COLORS.muted }}>
            {t("checkout.payment_hint")}
          </p>
          {paypalCfg && !paypalCfg.configured && (
            <p className="text-[10px] text-center mt-2" style={{ color: COLORS.chili }} data-testid="paypal-not-configured">
              Paiement temporairement indisponible · Contactez-nous au +41 27 346 07 75
            </p>
          )}
        </aside>
      </form>
    </div>
  );
}

// ============== Écran paiement PayPal (Smart Buttons) + TWINT (Phase 101ey) ==============
function PayPalPaymentScreen({ order, items, deliveryFee, paypalCfg, twintCfg, onCancel, onPaid, onTwintInitiated }) {
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [twintOpen, setTwintOpen] = useState(false);

  // Le backend renvoie déjà le total calculé (avec frais de port + arrondis Swiss) — on l'utilise tel quel
  const total = Number(order.total_ttc ?? 0);
  const subtotal = Number(order.subtotal_ttc ?? 0);
  const shipping = Number(deliveryFee ?? order.delivery_fee ?? 0);

  const twintAvailable = Boolean(twintCfg?.enabled && twintCfg?.configured);
  const paypalAvailable = Boolean(paypalCfg?.configured && paypalCfg?.client_id);

  if (!paypalAvailable && !twintAvailable) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="paypal-config-error">
        <p className="font-heading text-3xl mb-4" style={{ color: COLORS.primary }}>Paiement indisponible</p>
        <p className="text-sm mb-6" style={{ color: COLORS.secondary }}>
          Le paiement en ligne n'est pas encore configuré. Contactez-nous au +41 27 346 07 75
          ou par email à info@maison-thai.pro pour finaliser votre commande {order.reference}.
        </p>
        <button onClick={onCancel} className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.chili }}>
          Retour au formulaire
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 py-8 sm:py-12" data-testid="paypal-payment-screen">
      <button onClick={onCancel} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-6 sm:mb-8" style={{ color: COLORS.muted }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Modifier la commande
      </button>

      <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-3" style={{ color: COLORS.primary }}>Paiement sécurisé</h1>
      <p className="font-body text-sm sm:text-base mb-6 sm:mb-8" style={{ color: COLORS.secondary }}>
        Commande <strong style={{ color: COLORS.gold }}>{order.reference}</strong> · Montant à payer : <strong style={{ color: COLORS.gold }}>{total.toFixed(2)} CHF</strong>
        <br />
        Vous restez sur notre site. PayPal ouvre une fenêtre sécurisée pour finaliser le paiement.
      </p>

      {/* Récap commande — depuis les items du panier (pas encore vidé) */}
      <div className="mb-6 sm:mb-8 p-4 sm:p-5 border" style={{ borderColor: "rgba(242,236,228,0.12)", background: COLORS.surface }}>
        {(items || []).map((it, idx) => (
          <div key={idx} className="flex justify-between py-1.5 text-sm" style={{ color: COLORS.secondary }}>
            <span>{it.quantity} × {it.name}</span>
            <span className="font-mono" style={{ color: COLORS.primary }}>{((it.unit_price || 0) * it.quantity).toFixed(2)} CHF</span>
          </div>
        ))}
        {shipping > 0 && (
          <div className="flex justify-between py-1.5 text-sm" style={{ color: COLORS.secondary }}>
            <span>Frais de livraison</span>
            <span className="font-mono" style={{ color: COLORS.primary }}>{shipping.toFixed(2)} CHF</span>
          </div>
        )}
        <div className="flex justify-between pt-3 mt-3 border-t text-base" style={{ borderColor: "rgba(242,236,228,0.12)" }}>
          <strong style={{ color: COLORS.primary }}>Total</strong>
          <strong className="font-mono" style={{ color: COLORS.gold }}>{total.toFixed(2)} CHF</strong>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 border text-sm flex items-start gap-3" style={{ borderColor: COLORS.chili, color: COLORS.chili, background: "rgba(229,77,46,0.08)" }} data-testid="paypal-error">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {processing && (
        <div className="mb-6 flex items-center justify-center gap-3 py-4" style={{ color: COLORS.gold }} data-testid="paypal-processing">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-xs uppercase tracking-[0.2em]">Validation du paiement…</span>
        </div>
      )}

      <div data-testid="paypal-buttons-container">
        {twintAvailable && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setTwintOpen(true)}
              disabled={processing}
              className="w-full flex items-center justify-center gap-3 py-3 border-2 transition-colors disabled:opacity-50 group"
              style={{ borderColor: "#EE1E5A", background: "#EE1E5A", color: "#FFFFFF", minHeight: 48 }}
              data-testid="checkout-twint-btn"
            >
              <span className="font-bold text-lg tracking-wide">TWINT</span>
              <span className="text-xs opacity-90">Payer avec l&apos;app mobile suisse</span>
            </button>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ background: "rgba(242,236,228,0.12)" }} />
              <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>ou</span>
              <div className="flex-1 h-px" style={{ background: "rgba(242,236,228,0.12)" }} />
            </div>
          </div>
        )}

        {twintOpen && (
          <TwintPaymentModal
            order={order}
            amount={total}
            onClose={() => setTwintOpen(false)}
            onInitiated={() => { setTwintOpen(false); onTwintInitiated && onTwintInitiated(); }}
          />
        )}

        {paypalAvailable && (
        <PayPalScriptProvider
          options={{
            "client-id": paypalCfg.client_id,
            currency: "CHF",
            intent: "capture",
            components: "buttons",
            locale: "fr_CH",
            // Phase 98j — Activer explicitement le funding CB pour rendre le bouton
            // "Carte bancaire" visible séparément (sinon caché derrière le PayPal SDK
            // selon la config du compte marchand). Désactive les moyens non-souhaités.
            "enable-funding": "card",
            "disable-funding": "credit,paylater",
          }}
        >
          {/* Handlers partagés PayPal & Carte — mêmes create/capture pour les 2 boutons */}
          {(() => {
            const commonProps = {
              style: { layout: "vertical", shape: "rect", label: "pay", height: 48 },
              disabled: processing,
              createOrder: async () => {
                setError("");
                const res = await fetch(`${API}/api/shop/paypal/create-order`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ order_id: order.id, return_base_url: window.location.origin }),
                });
                if (!res.ok) {
                  const data = await res.json().catch(() => ({}));
                  throw new Error(data.detail || "Impossible d'initier le paiement PayPal");
                }
                const data = await res.json();
                return data.paypal_order_id;
              },
              onApprove: async (data) => {
                setProcessing(true);
                try {
                  const res = await fetch(`${API}/api/shop/paypal/capture`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ order_id: order.id, paypal_order_id: data.orderID }),
                  });
                  if (!res.ok) {
                    let detail = "";
                    try {
                      const errData = await res.json();
                      detail = errData.detail || JSON.stringify(errData);
                    } catch {
                      detail = await res.text().catch(() => "");
                    }
                    console.error("PayPal capture failed", res.status, detail, "paypal_order_id:", data.orderID);
                    throw new Error(`Capture échouée (HTTP ${res.status}). ${detail.slice(0, 250)}\n\nRéf. PayPal : ${data.orderID}`);
                  }
                  const cap = await res.json();
                  if (cap.paid) {
                    onPaid();
                  } else {
                    setError(`Paiement non finalisé (statut PayPal : ${cap.paypal_status}). Si le montant a été débité, contactez-nous avec la référence ${order.reference} — Réf. PayPal ${data.orderID}.`);
                    setProcessing(false);
                  }
                } catch (e) {
                  setError(e.message);
                  setProcessing(false);
                }
              },
              onError: (err) => {
                console.error("PayPal error:", err);
                setError(`Erreur PayPal : ${err?.message || String(err) || "inconnue"}. Vérifiez que vous n'êtes pas en navigation privée bloquant les cookies tiers, et réessayez.`);
              },
              onCancel: () => {
                setError("Paiement annulé. Vous pouvez réessayer ou modifier votre commande.");
              },
            };
            return (
              <>
                {/* Bouton PayPal officiel */}
                <PayPalButtons {...commonProps} fundingSource={FUNDING.PAYPAL} />
                {/* Bouton Carte bancaire séparé — Phase 98j */}
                <div className="mt-3" data-testid="paypal-card-button-wrapper">
                  <PayPalButtons {...commonProps} fundingSource={FUNDING.CARD} />
                </div>
              </>
            );
          })()}
        </PayPalScriptProvider>
        )}
      </div>

      <p className="text-[10px] uppercase tracking-[0.2em] text-center mt-8" style={{ color: COLORS.muted }}>
        Paiement sécurisé · Carte bancaire · Compte PayPal{twintAvailable ? " · TWINT" : ""} · QR code mobile
        {paypalCfg?.mode === "sandbox" && <span style={{ color: COLORS.gold }}> · MODE TEST</span>}
      </p>
    </div>
  );
}


function Field({ icon, label, value, onChange, type = "text", testid, autoComplete, placeholder }) {  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: COLORS.muted }}>{label}</span>
      <div className="flex items-center border focus-within:border-white/30 transition-colors" style={{ borderColor: "rgba(242,236,228,0.12)" }}>
        {icon && <span className="pl-3" style={{ color: COLORS.muted }}>{icon}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          data-testid={testid}
          className="flex-1 bg-transparent px-3 py-3 text-sm focus:outline-none"
          style={{ color: COLORS.primary }}
        />
      </div>
    </label>
  );
}

// ============== Confirmation page ==============
export function ConfirmationPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/shop/orders/${id}`)
      .then((r) => { if (!r.ok) throw new Error("404"); return r.json(); })
      .then(setOrder)
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <p className="font-heading text-3xl mb-4" style={{ color: COLORS.primary }}>Commande introuvable</p>
      <Link to="/boutique" className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.chili }}>Retour au catalogue</Link>
    </div>
  );
  if (!order) return <div className="min-h-[40vh] flex items-center justify-center text-xs uppercase tracking-widest" style={{ color: COLORS.muted }}>Chargement…</div>;

  const total = (order.lines || []).reduce((s, l) => s + (l.unit_price || 0) * l.quantity, 0);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="confirmation-page">
      <div className="text-center mb-12">
        <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-6" style={{ background: "rgba(136,160,102,0.15)" }}>
          <Check className="w-6 h-6" style={{ color: COLORS.basil }} />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl mb-3" style={{ color: COLORS.primary }}>
          Merci{order.customer_name ? `, ${order.customer_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="font-body text-base" style={{ color: COLORS.secondary }}>
          Votre commande <strong style={{ color: COLORS.gold }}>{order.reference}</strong> est bien enregistrée.<br />
          Nous vous contactons sous peu pour confirmer les détails et le paiement.
        </p>
      </div>

      <div className="border p-6 md:p-8" style={{ borderColor: "rgba(242,236,228,0.1)", background: COLORS.surface }}>
        <h2 className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: COLORS.muted }}>Détail</h2>
        {(order.lines || []).map((l) => (
          <div key={l.id} className="flex justify-between py-2 border-b text-sm" style={{ borderColor: "rgba(242,236,228,0.06)" }}>
            <span style={{ color: COLORS.primary }}>{l.quantity} × {l.product_name}</span>
            <span className="font-mono" style={{ color: COLORS.primary }}>{((l.unit_price || 0) * l.quantity).toFixed(2)} CHF</span>
          </div>
        ))}
        <div className="flex justify-between pt-4 text-lg">
          <span style={{ color: COLORS.primary }}>Total TTC</span>
          <span className="font-mono font-semibold" style={{ color: COLORS.gold }}>{total.toFixed(2)} CHF</span>
        </div>
      </div>

      <div className="text-center mt-12 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>
          Une question ? <a href="mailto:info@maison-thai.pro" className="hover:underline" style={{ color: COLORS.gold }}>info@maison-thai.pro</a> · +41 27 346 07 75
        </p>
        <Link to="/boutique" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em] border" style={{ borderColor: "rgba(242,236,228,0.2)", color: COLORS.primary }}>
          Retour au catalogue
        </Link>
      </div>
    </div>
  );
}


// ============== Page retour paiement — succès ==============
export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [tx, setTx] = useState(null);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    if (!orderId) { setError("Identifiant de commande manquant"); setPolling(false); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/shop/paypal/transaction/${orderId}`);
        if (!r.ok) throw new Error("Impossible de vérifier la transaction");
        const data = await r.json();
        if (cancelled) return;
        setTx(data);
        // Stop polling dès que payé ou après 6 essais (~12 secondes)
        if (data.paid || data.internal_status === "PAIEMENT_ECHOUE" || attempts >= 6) {
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        if (!cancelled) { setError(e.message); setPolling(false); }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [orderId]);

  if (error) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-error">
      <p className="font-heading text-3xl mb-4" style={{ color: COLORS.primary }}>Erreur</p>
      <p className="text-sm mb-6" style={{ color: COLORS.secondary }}>{error}</p>
      <Link to="/boutique" className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.chili }}>Retour au catalogue</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="payment-success-page">
      <div className="text-center mb-12">
        <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-6" style={{ background: tx?.paid ? "rgba(136,160,102,0.15)" : "rgba(193,160,99,0.15)" }}>
          {tx?.paid ? <Check className="w-6 h-6" style={{ color: COLORS.basil }} /> : <Loader2 className="w-6 h-6 animate-spin" style={{ color: COLORS.gold }} />}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl mb-3" style={{ color: COLORS.primary }} data-testid="payment-success-title">
          {tx?.paid ? "Paiement confirmé" : (polling ? "Vérification du paiement…" : "Paiement en cours de traitement")}
        </h1>
        {tx?.reference && (
          <p className="font-body text-base" style={{ color: COLORS.secondary }}>
            Commande <strong style={{ color: COLORS.gold }}>{tx.reference}</strong>
            {tx.paid && <><br />Un email de confirmation arrive sous peu.</>}
            {!tx.paid && !polling && <><br />Si vous avez payé, vous recevrez la confirmation par email dès validation par notre banque.</>}
          </p>
        )}
      </div>

      <div className="text-center mt-12 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>
          Une question ? <a href="mailto:info@maison-thai.pro" className="hover:underline" style={{ color: COLORS.gold }}>info@maison-thai.pro</a> · +41 27 346 07 75
        </p>
        <Link to="/boutique" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em] border" style={{ borderColor: "rgba(242,236,228,0.2)", color: COLORS.primary }} data-testid="back-to-shop-btn">
          Retour au catalogue
        </Link>
      </div>
    </div>
  );
}

// ============== Page retour paiement — échec / annulation ==============
export function PaymentFailurePage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="payment-failure-page">
      <div className="text-center mb-12">
        <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-6" style={{ background: "rgba(229,77,46,0.15)" }}>
          <AlertCircle className="w-6 h-6" style={{ color: COLORS.chili }} />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl mb-3" style={{ color: COLORS.primary }} data-testid="payment-failure-title">
          Paiement non finalisé
        </h1>
        <p className="font-body text-base max-w-xl mx-auto" style={{ color: COLORS.secondary }}>
          Le paiement n'a pas pu être finalisé. Aucun montant n'a été débité.
          {orderId && <><br />Votre panier <strong style={{ color: COLORS.gold }}>{orderId.slice(0, 8)}</strong> est conservé.</>}
        </p>
      </div>

      <div className="text-center mt-12 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>
          Besoin d'aide ? <a href="mailto:info@maison-thai.pro" className="hover:underline" style={{ color: COLORS.gold }}>info@maison-thai.pro</a> · +41 27 346 07 75
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link to="/boutique/checkout" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em]" style={{ background: COLORS.chili, color: COLORS.primary }} data-testid="retry-payment-btn">
            Réessayer le paiement
          </Link>
          <Link to="/boutique" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em] border" style={{ borderColor: "rgba(242,236,228,0.2)", color: COLORS.primary }}>
            Retour au catalogue
          </Link>
        </div>
      </div>
    </div>
  );
}


// ============== Modale TWINT (Phase 101ey) ==============
// Détecte mobile vs desktop :
//   - mobile → deep link vers l'app TWINT
//   - desktop → QR code à scanner + rappel du montant + référence
function TwintPaymentModal({ order, amount, onClose, onInitiated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [twintData, setTwintData] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  const isMobile = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    shopApi.post("/twint/initiate", { order_id: order.id })
      .then((r) => { if (!cancelled) setTwintData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e?.message || "Impossible d'initier TWINT";
        setError(typeof detail === "string" ? detail : "Impossible d'initier TWINT");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [order.id]);

  const openTwintApp = () => {
    if (twintData?.payment_link) {
      window.location.href = twintData.payment_link;
    }
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(twintData?.reference || "");
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2000);
    } catch { /* silent */ }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid="twint-modal"
    >
      <div
        className="relative w-full max-w-md border overflow-y-auto max-h-[90vh]"
        style={{ background: COLORS.surface, borderColor: "rgba(242,236,228,0.15)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5"
          style={{ color: COLORS.muted }}
          data-testid="twint-close"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-3" style={{ background: "#EE1E5A", color: "#FFFFFF" }}>
              <span className="font-bold tracking-wider">TWINT</span>
            </div>
            <h2 className="font-heading text-2xl mb-1" style={{ color: COLORS.primary }}>
              Paiement TWINT
            </h2>
            <p className="text-xs uppercase tracking-[0.15em]" style={{ color: COLORS.muted }}>
              Application mobile suisse
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8 gap-3" style={{ color: COLORS.gold }} data-testid="twint-loading">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-xs uppercase tracking-[0.2em]">Préparation…</span>
            </div>
          )}

          {error && (
            <div className="p-3 border text-xs flex items-start gap-2 mb-4" style={{ borderColor: COLORS.chili, color: COLORS.chili, background: "rgba(229,77,46,0.08)" }} data-testid="twint-error">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {twintData && !loading && !error && (
            <>
              {/* Montant + référence, toujours affichés */}
              <div className="mb-6 p-4 border" style={{ borderColor: "rgba(242,236,228,0.12)", background: COLORS.bg }}>
                <div className="flex justify-between items-baseline mb-3">
                  <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>Montant à payer</span>
                  <span className="font-mono text-2xl font-semibold" style={{ color: COLORS.gold }} data-testid="twint-amount">
                    {Number(twintData.amount_chf).toFixed(2)} CHF
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>Référence (à saisir en note TWINT)</div>
                    <div className="font-mono text-base mt-0.5" style={{ color: COLORS.primary }} data-testid="twint-reference">
                      {twintData.reference}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={copyReference}
                    className="p-2 border"
                    style={{ borderColor: "rgba(242,236,228,0.2)", color: refCopied ? COLORS.basil : COLORS.primary }}
                    aria-label="Copier la référence"
                    data-testid="twint-copy-ref"
                  >
                    {refCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {isMobile ? (
                <button
                  type="button"
                  onClick={openTwintApp}
                  className="w-full flex items-center justify-center gap-2 py-4 mb-3"
                  style={{ background: "#EE1E5A", color: "#FFFFFF", minHeight: 52 }}
                  data-testid="twint-open-app"
                >
                  <Smartphone className="w-5 h-5" />
                  <span className="font-semibold tracking-wide">Ouvrir l&apos;app TWINT</span>
                </button>
              ) : (
                <div className="flex flex-col items-center gap-3 mb-4">
                  <div className="p-3 bg-white" data-testid="twint-qr-code">
                    <QRCodeCanvas value={twintData.payment_link} size={192} level="M" />
                  </div>
                  <div className="text-center text-xs" style={{ color: COLORS.secondary }}>
                    <div className="mb-1 flex items-center justify-center gap-1.5">
                      <QrCode className="w-3.5 h-3.5" style={{ color: COLORS.gold }} />
                      <span>Scanne ce QR avec l&apos;app TWINT de ton téléphone</span>
                    </div>
                    <div className="text-[10px]" style={{ color: COLORS.muted }}>
                      Saisis le montant <strong style={{ color: COLORS.gold }}>{Number(twintData.amount_chf).toFixed(2)} CHF</strong>
                      {" "}puis colle la référence en note.
                    </div>
                  </div>
                </div>
              )}

              <ol className="text-[11px] mb-5 space-y-1.5" style={{ color: COLORS.secondary }}>
                <li>1. {isMobile ? "L'app TWINT s'ouvre pré-remplie avec Maison Thaï" : "Scanne le QR sur ton mobile avec l'app TWINT"}</li>
                <li>2. Saisis le montant exact et ajoute la référence en note</li>
                <li>3. Valide le paiement dans TWINT</li>
                <li>4. Reviens ici et clique sur « J&apos;ai payé »</li>
              </ol>

              <button
                type="button"
                onClick={onInitiated}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 mb-3"
                style={{ borderColor: COLORS.basil, color: COLORS.basil, background: "rgba(136,160,102,0.08)" }}
                data-testid="twint-mark-paid"
              >
                <Check className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em]">J&apos;ai payé — confirmer</span>
              </button>

              <p className="text-[10px] text-center leading-relaxed" style={{ color: COLORS.muted }}>
                Nous vérifions la réception dans les 24 h et validons ta commande manuellement.
                Sans virement reçu, la commande sera automatiquement annulée après 3 jours.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============== Page retour TWINT — en attente confirmation (Phase 101ey) ==============
export function TwintPendingPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="twint-pending-page">
      <div className="text-center mb-12">
        <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-6" style={{ background: "rgba(238,30,90,0.15)" }}>
          <Loader2 className="w-6 h-6" style={{ color: "#EE1E5A" }} />
        </div>
        <h1 className="font-heading text-4xl md:text-5xl mb-3" style={{ color: COLORS.primary }} data-testid="twint-pending-title">
          Paiement TWINT en cours de vérification
        </h1>
        <p className="font-body text-base max-w-xl mx-auto" style={{ color: COLORS.secondary }}>
          Merci ! Nous vérifions la réception du paiement dans notre compte TWINT et validons ta commande sous 24 h.
          {orderId && <><br />Réf. : <strong className="font-mono" style={{ color: COLORS.gold }}>{orderId.slice(0, 8)}</strong></>}
        </p>
      </div>

      <div className="p-5 border mb-8" style={{ borderColor: "rgba(238,30,90,0.35)", background: "rgba(238,30,90,0.05)" }}>
        <div className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#EE1E5A" }}>Prochaines étapes</div>
        <ul className="text-sm space-y-1.5" style={{ color: COLORS.secondary }}>
          <li>• Tu recevras un email dès que le paiement est validé (généralement le jour même)</li>
          <li>• Si le paiement TWINT n&apos;est pas passé, réessaie ou contacte-nous</li>
          <li>• Sans virement reçu sous 3 jours, la commande est automatiquement annulée</li>
        </ul>
      </div>

      <div className="text-center mt-12 space-y-4">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.muted }}>
          Une question ? <a href="mailto:info@maison-thai.pro" className="hover:underline" style={{ color: COLORS.gold }}>info@maison-thai.pro</a> · +41 27 346 07 75
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link to="/boutique/mon-compte/commandes" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em]" style={{ background: COLORS.gold, color: COLORS.bg }} data-testid="twint-pending-my-orders">
            Voir mes commandes
          </Link>
          <Link to="/boutique" className="inline-block py-3 px-8 text-xs uppercase tracking-[0.25em] border" style={{ borderColor: "rgba(242,236,228,0.2)", color: COLORS.primary }}>
            Retour au catalogue
          </Link>
        </div>
      </div>
    </div>
  );
}
